import type { HTMLAttributes, ReactNode, ElementType } from 'react';
import { cn } from './utils';

export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type TextColor = 'default' | 'muted' | 'accent' | 'primary' | 'inverse' | 'error' | 'success';
export type TextAs = 'p' | 'span' | 'div' | 'label' | 'li';

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: TextAs;
  size?: TextSize;
  weight?: TextWeight;
  color?: TextColor;
  italic?: boolean;
  children: ReactNode;
}

const sizeStyles: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

const weightStyles: Record<TextWeight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const colorStyles: Record<TextColor, string> = {
  default: 'text-neutral-800',
  muted: 'text-neutral-600',
  accent: 'text-accent',
  primary: 'text-primary',
  inverse: 'text-white',
  error: 'text-error-fg',
  success: 'text-success-fg',
};

const baseStyles = 'font-body';

export function Text({
  as = 'p',
  size = 'base',
  weight = 'regular',
  color = 'default',
  italic = false,
  className = '',
  children,
  ...rest
}: TextProps) {
  const Tag = as as ElementType;
  const cls = cn(
    baseStyles,
    sizeStyles[size],
    weightStyles[weight],
    colorStyles[color],
    italic && 'italic',
    className,
  );
  return (
    <Tag className={cls} {...rest}>
      {children}
    </Tag>
  );
}
